import type { SapMappingHealth, SapTargetFieldDef } from '@hcc/types';
import { Pill } from '../../../components/ui/Pill';
import { FieldPicker } from './FieldPicker';
import { ValueMapEditor } from './ValueMapEditor';
import type { MappingDraft, SapFormSummary } from './mappingTypes';

const HEALTH_TONE: Record<SapMappingHealth['status'], 'success' | 'neutral' | 'danger' | 'warning'> = {
  ok: 'success',
  unmapped: 'neutral',
  form_missing: 'danger',
  field_missing: 'danger',
  option_drift: 'warning',
};

/** One SAP target field: source form + field pickers and, for code
 *  fields, the per-option value translation. */
export function MappingRow({
  def,
  draft,
  forms,
  health,
  onChange,
}: {
  def: SapTargetFieldDef;
  draft: MappingDraft;
  forms: SapFormSummary[];
  health?: SapMappingHealth;
  onChange: (next: MappingDraft) => void;
}) {
  const form = forms.find((f) => f.key === draft.form_key);
  const field = form?.fields.find((f) => f.key === draft.field_key);

  return (
    <div className="px-4 py-3 sm:px-5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium">
            {def.label}
            {def.required ? <span className="ml-1 text-danger">*</span> : null}
          </div>
          <div className="text-xs text-fg-dim">{def.id}</div>
        </div>
        {health ? (
          <Pill tone={HEALTH_TONE[health.status]}>{health.status.replace('_', ' ')}</Pill>
        ) : null}
      </div>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <select
          className="h-9 rounded-token-sm border border-line/20 bg-surface px-2 text-sm sm:w-52 sm:shrink-0"
          value={draft.form_key ?? ''}
          onChange={(e) =>
            onChange({ form_key: e.target.value || null, field_key: null, value_map: {} })
          }
        >
          <option value="">No form</option>
          {forms.map((f) => (
            <option key={f.key} value={f.key}>
              {f.title}
            </option>
          ))}
        </select>
        <div className="min-w-0 flex-1">
          <FieldPicker
            fields={form?.fields ?? []}
            value={draft.field_key}
            disabled={!form}
            addressPart={def.addressPart ?? null}
            onChange={(field_key) => onChange({ ...draft, field_key, value_map: {} })}
          />
        </div>
      </div>
      {health?.detail ? <p className="mt-1 text-xs text-danger-soft-fg">{health.detail}</p> : null}
      {def.kind === 'code' && def.codeOptions && field ? (
        <div className="mt-2 rounded-token-sm bg-surface-2 p-3">
          <ValueMapEditor
            field={field}
            codeOptions={def.codeOptions}
            valueMap={draft.value_map}
            onChange={(value_map) => onChange({ ...draft, value_map })}
          />
        </div>
      ) : null}
    </div>
  );
}
